"use client";

import { AnimatePresence, motion } from "motion/react";
import { CanvasPanelFooter } from "./canvas-panel-footer";
import { WebPreview } from "./web-preview";
import { WorkspaceState } from "./workspace-state";
import { CodePanel } from "./code-panel";
import { cn } from "@/lib/utils";
import { CanvasPanelTopBar } from "./canvas-panel-top-bar";
import { PendingChangesBar } from "./pending-changes-bar";
import { UnsavedChangesDialog } from "./unsaved-changes-dialog";
import { useCanvasPanelController } from "./hooks/use-canvas-panel-controller";
import unicornSceneConfigDark from "@/components/ui/unicorn-studio/workspace-loader-dark.json";
import { useEffect, useMemo, useRef, useState } from "react";
import { SafeUnicornScene } from "../ui/unicorn-studio/safe-unicorn-scene";
import { useFetchProject } from "@/hooks/use-fetch-project";
import { useProjectWorkspacePreview } from "@/hooks/use-project-workspace-preview";
import { imagineStore } from "@/stores/imagine.store";
import { useSandboxHealthPing } from "@/hooks/use-sandbox-health-ping";
import { useDebounce } from "@uidotdev/usehooks";

export function CanvasPanel({
  projectId,
}: {
  isGeneratingNewProject: boolean;
  projectId: string;
}) {
  const project = imagineStore.use.project();
  const isReadonly = imagineStore.use.isReadonly();
  const {
    previewUrlWithPreviewToken,
    previewUrl,
    isInProgress,
    uiMode,
    displayWorkspaceStateOverlay,
    codeSelectedFile,
    setCodeSelectedFile,
    onRequestModeChange,
    pendingChangesBarProps,
    unsavedDialogProps,
  } = useCanvasPanelController();

  const debouncedDisplayWorkspaceStateOverlay = useDebounce(
    displayWorkspaceStateOverlay,
    1000,
  );
  const unicornContainerRef = useRef<HTMLDivElement>(null);
  const [unicornSceneKey, setUnicornSceneKey] = useState(0);
  const workspaceStateError = imagineStore.use.workspaceState.error();
  useFetchProject();
  useSandboxHealthPing();
  const projectWorkspacePreview = useProjectWorkspacePreview({
    disabled: isReadonly,
  });

  useEffect(() => {
    const container = unicornContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Force UnicornScene to rerender when parent size changes
      setUnicornSceneKey((prev) => prev + 1);
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (
      project &&
      isReadonly &&
      project.appwriteSiteUrl === null &&
      !projectWorkspacePreview.isFetched
    ) {
      projectWorkspacePreview.refetch();
    }
  }, [project, isReadonly, projectWorkspacePreview.isFetched]);

  const initialWebPreviewUrl = useMemo(() => {
    if (!project) return null;

    if (isReadonly && project.appwriteSiteUrl) {
      return new URL(project.appwriteSiteUrl);
    } else {
      return previewUrlWithPreviewToken;
    }
  }, [projectId, isReadonly, previewUrlWithPreviewToken, project, previewUrl]);

  return (
    <div className="relative h-full w-full flex flex-col">
      <CanvasPanelTopBar onRequestModeChange={onRequestModeChange} />
      <div className="flex-1 w-full relative">
        {/* Preview mode - absolute positioning for both preview and footer */}
        <div
          className={cn(
            uiMode === "preview"
              ? "absolute inset-0 flex flex-col opacity-100 pointer-events-auto"
              : "absolute inset-0 flex flex-col opacity-0 pointer-events-none",
          )}
        >
          <div className="flex-1 w-full flex justify-center items-center relative">
            <AnimatePresence mode="wait">
              {debouncedDisplayWorkspaceStateOverlay && (
                <motion.div
                  key="preview-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{
                    opacity: 0,
                    transition: { delay: 1.0, duration: 0.3 },
                  }}
                  className="absolute h-full w-full flex flex-col items-center justify-center z-20 dark"
                >
                  <AnimatePresence mode="wait">
                    {(isInProgress || workspaceStateError) && (
                      <motion.div
                        className="z-20"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                          opacity: 0,
                          y: 20,
                          transition: { delay: 1.0, duration: 0.3 },
                        }}
                      >
                        <WorkspaceState />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div
                    ref={unicornContainerRef}
                    className="absolute inset-0 z-10 h-full w-full bg-background overflow-hidden"
                  >
                    <SafeUnicornScene
                      key={unicornSceneKey}
                      rawJson={unicornSceneConfigDark}
                      width="100%"
                      height="100%"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {initialWebPreviewUrl && (
              <WebPreview
                key={`project-${projectId}`}
                initialUrl={initialWebPreviewUrl}
              />
            )}
          </div>
          {!isReadonly && <CanvasPanelFooter />}
        </div>

        {/* Code mode - flex layout for code panel and footer */}
        {!isReadonly && (
          <div
            className={cn(
              "absolute inset-0 flex flex-col",
              uiMode === "code"
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none",
            )}
          >
            <div className="flex-1 min-h-0">
              <CodePanel
                isActive={uiMode === "code"}
                selectedFile={codeSelectedFile}
                onSelectedFileChange={setCodeSelectedFile}
              />
            </div>
            <CanvasPanelFooter />
          </div>
        )}
      </div>

      {!isReadonly && (
        <>
          <PendingChangesBar {...pendingChangesBarProps} />
          <UnsavedChangesDialog {...unsavedDialogProps} />
        </>
      )}
    </div>
  );
}
